import { useState, useRef, useEffect } from 'react';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { RadioButton } from 'primereact/radiobutton';

import { Dialog, Button, SkeletonLoader } from 'components';

import { useListOneApprovalQuery } from '../../queries';
import {
  useUpdateApprovalCaseMutation,
  useUploadApprovalDocumentMutation,
  useDeleteApprovalDocumentMutation,
  useApproveListOneTicketMutation,
} from '../../mutations';

import {
  ApprovalBranch,
  ApprovalDocumentItem,
  FetchApprovalApiResponse,
  UploadedApprovalDocument,
} from '../../api/api.types';

interface ListOneDocumentsDialogProps {
  visible: boolean;
  ticketId: string;
  onHide: () => void;
  onApproved: (response: any) => void;
}

// The insurer publishes a blank medical form per country. The agent downloads
// it, fills it in with the member, and uploads it back against the same item.
// A country or item without a blank simply shows no link.
const BLANK_FORMS: Record<string, Record<string, string>> = {
  uae: {
    'medical-application-form': '/forms/UAE-MAF.pdf',
  },
  ksa: {
    hdf: '/forms/KSA-MAF.pdf',
    // the second alternative here is an employment contract with the form
    'gosi-or-contract-hdf': '/forms/KSA-MAF.pdf',
    // both tabs live in the one enrolment workbook
    'addition-form-excel': '/forms/KSA-Medical-Enrollment-Orient.xlsb',
    'deletion-form-excel': '/forms/KSA-Medical-Enrollment-Orient.xlsb',
  },
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
// The Saudi cases are filled in Excel and sent back as a workbook. Browsers often
// report .xlsb with no useful type, so the extension decides those.
const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.xlsb',
  '.xlsx',
  '.xls',
];

// The document names the agent sees are plain descriptions, so the ticket type
// is spelled out here rather than shown as the two letter code the backend uses.
const TICKET_TYPE_LABELS: Record<string, string> = {
  AD: 'Addition',
  DE: 'Deletion',
};

const ticketTypeLabel = (type: string) => TICKET_TYPE_LABELS[type] || type;

const isAcceptedFile = (file: File) => {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const name = (file.name || '').toLowerCase();
  return ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension));
};

// The whole branch for every sub case comes back with the ticket, so switching a
// choice re-draws the list straight away and the save happens in the background.
const branchFor = (
  approval: FetchApprovalApiResponse,
  subCase: string | null
): ApprovalBranch | null => {
  const branches = approval.branches || {};
  return branches[subCase || 'default'] || null;
};

// A question is only answered once the agent has picked yes or no. Nothing is
// assumed on their behalf, so an untouched question keeps the ticket on hold.
const isAnswered = (answers: Record<string, boolean>, key: string) =>
  typeof answers[key] === 'boolean';

export default function ListOneDocumentsDialog({
  visible,
  ticketId,
  onHide,
  onApproved,
}: ListOneDocumentsDialogProps) {
  const [subCase, setSubCase] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [fileError, setFileError] = useState('');
  const [pendingKey, setPendingKey] = useState('');
  const seededTicketId = useRef('');

  const approvalQuery = useListOneApprovalQuery(ticketId);
  const updateApprovalCaseMutation = useUpdateApprovalCaseMutation({
    onSuccess: () => approvalQuery.refetch(),
  });
  const uploadApprovalDocumentMutation = useUploadApprovalDocumentMutation({
    onSuccess: handleDocumentChangeSuccess,
    onError: handleDocumentChangeError,
  });
  const deleteApprovalDocumentMutation = useDeleteApprovalDocumentMutation({
    onSuccess: handleDocumentChangeSuccess,
    onError: handleDocumentChangeError,
  });
  const approveListOneTicketMutation = useApproveListOneTicketMutation({
    onSuccess: handleApproveSuccess,
    onError: handleApproveError,
  });

  const approval: FetchApprovalApiResponse | undefined =
    approvalQuery.data?.data;
  const branch = approval ? branchFor(approval, subCase) : null;
  const activeQuestion = branch?.questions?.[0] || null;
  const items: ApprovalDocumentItem[] = branch
    ? activeQuestion && answers[activeQuestion.key] === true
      ? branch.itemsWithQuestion
      : branch.items
    : approval?.items || [];
  const documents: UploadedApprovalDocument[] = approval?.documents || [];
  const documentFor = (key: string) =>
    documents.find((entry) => entry.key === key) || null;

  const unansweredQuestions = (branch?.questions || []).filter(
    (question) => !isAnswered(answers, question.key)
  );
  const missing = items.filter((item) => !documentFor(item.key));
  const uploadedCount = items.length - missing.length;
  const isApproved = Boolean(approval?.approvedAt);
  const isSaving =
    updateApprovalCaseMutation.isLoading ||
    uploadApprovalDocumentMutation.isLoading ||
    deleteApprovalDocumentMutation.isLoading ||
    approvalQuery.isFetching;
  const isApproving = approveListOneTicketMutation.isLoading;

  // The dialog is reused for every row, so nothing is carried over from the
  // ticket the agent looked at before this one.
  useEffect(() => {
    seededTicketId.current = '';
    setSubCase(null);
    setAnswers({});
    setFileError('');
    setPendingKey('');
  }, [ticketId]);

  // Seed the agent's choices once per ticket from what was saved before, so a
  // background refresh after a save never overwrites what they are picking.
  useEffect(() => {
    if (!ticketId || !approval?.success) return;
    if (seededTicketId.current === ticketId) return;

    seededTicketId.current = ticketId;
    setSubCase(approval.subCase);
    setAnswers(approval.answers || {});
  }, [approval, ticketId]);

  function saveCase(
    nextSubCase: string | null,
    nextAnswers: Record<string, boolean>
  ) {
    updateApprovalCaseMutation.mutate({
      ticketId,
      subCase: nextSubCase,
      answers: nextAnswers,
    });
  }

  function handleSubCaseChange(value: string) {
    setSubCase(value);
    setFileError('');
    saveCase(value, answers);
  }

  function handleAnswerChange(key: string, value: boolean) {
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);
    setFileError('');
    saveCase(subCase, nextAnswers);
  }

  function handleUpload(documentKey: string, event: FileUploadHandlerEvent) {
    const file = event.files?.[0];
    event.options.clear();
    if (!file) return;

    if (!isAcceptedFile(file)) {
      setFileError('Only PDF, Excel, JPG and PNG files can be uploaded.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError('That file is too large. The largest we can take is 10 MB.');
      return;
    }

    setFileError('');
    setPendingKey(documentKey);
    uploadApprovalDocumentMutation.mutate({ ticketId, documentKey, file });
  }

  function handleRemove(documentKey: string) {
    setFileError('');
    setPendingKey(documentKey);
    deleteApprovalDocumentMutation.mutate({ ticketId, documentKey });
  }

  function handleDocumentChangeSuccess() {
    setPendingKey('');
    approvalQuery.refetch();
  }

  function handleDocumentChangeError() {
    setPendingKey('');
    setFileError('We could not save that file. Please try again.');
  }

  function handleApprove() {
    setFileError('');
    approveListOneTicketMutation.mutate({ ticketId });
  }

  function handleApproveSuccess(response: any) {
    // The list is checked again on the server, so a document that went missing
    // in the meantime sends us back to the checklist instead of closing.
    if (response?.data?.missing?.length > 0) {
      setFileError(
        'Some documents are still missing. The list below has been refreshed.'
      );
      approvalQuery.refetch();
      return;
    }

    onApproved(response);
    onHide();
  }

  function handleApproveError() {
    setFileError('We could not approve this ticket. Please try again.');
  }

  function handleFileSizeFail() {
    setFileError('That file is too large. The largest we can take is 10 MB.');
  }

  const renderChecklistItem = (item: ApprovalDocumentItem) => {
    const uploaded = documentFor(item.key);
    const isBusy = pendingKey === item.key && isSaving;
    const blankForm = approval
      ? BLANK_FORMS[approval.countryKey]?.[item.key]
      : undefined;

    return (
      <div className="az-documents__item" key={item.key}>
        <div className="az-documents__item-text">
          <span className="az-documents__item-label">{item.label}</span>

          {item.alternatives?.length > 1 && (
            <p className="az-documents__hint">
              Any one of these is fine: {item.alternatives.join(', or ')}.
            </p>
          )}

          {blankForm && (
            <a
              className="az-documents__blank-form"
              href={blankForm}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              Download blank form
            </a>
          )}

          {uploaded ? (
            <div className="az-documents__file">
              <span className="az-documents__file-name">
                {uploaded.filename}
              </span>
              <a
                className="az-documents__file-link"
                href={uploaded.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View
              </a>
              {!isApproved && (
                <Button
                  onClick={() => handleRemove(item.key)}
                  disabled={isBusy || isApproving}
                  text
                >
                  Remove
                </Button>
              )}
            </div>
          ) : null}
        </div>

        {!isApproved && (
          <FileUpload
            mode="basic"
            name="file"
            accept=".pdf,.xlsb,.xlsx,.xls,image/jpeg,image/png"
            maxFileSize={MAX_FILE_SIZE}
            chooseLabel={uploaded ? 'Replace file' : 'Choose file'}
            disabled={isBusy || isApproving}
            onValidationFail={handleFileSizeFail}
            uploadHandler={(event) => handleUpload(item.key, event)}
            customUpload
            auto
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (approvalQuery.isLoading) return <SkeletonLoader height="20rem" />;

    if (approvalQuery.isError || !approval?.success) {
      return (
        <p className="az-documents__message az-documents__message--warn">
          We could not load the document list for this ticket. Please close this
          window and try again.
        </p>
      );
    }

    return (
      <div className="az-documents">
        <div className="az-documents__summary">
          <div>
            <span className="az-documents__summary-label">Employee</span>
            <span className="az-documents__summary-value">
              {approval.ticket.employeeName || 'Not given'}
            </span>
          </div>
          <div>
            <span className="az-documents__summary-label">Staff ID</span>
            <span className="az-documents__summary-value">
              {approval.ticket.employeeId}
            </span>
          </div>
          <div>
            <span className="az-documents__summary-label">Country</span>
            <span className="az-documents__summary-value">
              {approval.ticket.country || 'Not given'}
            </span>
          </div>
          <div>
            <span className="az-documents__summary-label">Ticket</span>
            <span className="az-documents__summary-value">
              {ticketTypeLabel(approval.ticket.type)}
            </span>
          </div>
        </div>

        {isApproved && (
          <p className="az-documents__message az-documents__message--done">
            This ticket was already approved
            {approval.approvedByName ? ` by ${approval.approvedByName}` : ''}.
          </p>
        )}

        {approval.subCaseOptions.length > 0 && (
          <div className="az-documents__choice">
            <p className="az-documents__choice-label">Which case is this?</p>
            <div className="az-documents__choice-options">
              {approval.subCaseOptions.map((option) => (
                <label
                  className="az-documents__choice-option"
                  key={option.value}
                >
                  <RadioButton
                    name="subCase"
                    value={option.value}
                    checked={subCase === option.value}
                    onChange={() => handleSubCaseChange(option.value)}
                    disabled={isApproved || isApproving}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {(branch?.questions || []).map((question) => (
          <div className="az-documents__choice" key={question.key}>
            <p className="az-documents__choice-label">{question.label}</p>
            <div className="az-documents__choice-options">
              <label className="az-documents__choice-option">
                <RadioButton
                  name={question.key}
                  value="yes"
                  checked={answers[question.key] === true}
                  onChange={() => handleAnswerChange(question.key, true)}
                  disabled={isApproved || isApproving}
                />
                <span>Yes</span>
              </label>
              <label className="az-documents__choice-option">
                <RadioButton
                  name={question.key}
                  value="no"
                  checked={answers[question.key] === false}
                  onChange={() => handleAnswerChange(question.key, false)}
                  disabled={isApproved || isApproving}
                />
                <span>No</span>
              </label>
            </div>
          </div>
        ))}

        <div className="az-documents__items">
          {items.length === 0 ? (
            <p className="az-documents__message">
              No documents are needed for this ticket.
            </p>
          ) : (
            items.map(renderChecklistItem)
          )}
        </div>

        {fileError && (
          <p className="az-documents__message az-documents__message--warn">
            {fileError}
          </p>
        )}

        {!isApproved && (
          <p className="az-documents__message">
            {missing.length === 0 && unansweredQuestions.length === 0
              ? 'Everything is here. This ticket can be approved and sent.'
              : [
                  missing.length > 0
                    ? `${uploadedCount} of ${items.length} documents uploaded.`
                    : '',
                  unansweredQuestions.length > 0
                    ? 'Answer the question above.'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
          </p>
        )}
      </div>
    );
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        outlined
        onClick={onHide}
        disabled={isApproving}
      />
      <Button
        label="Approve"
        onClick={handleApprove}
        loading={isApproving}
        disabled={
          !approval?.success ||
          isApproved ||
          isSaving ||
          missing.length > 0 ||
          unansweredQuestions.length > 0
        }
      />
    </div>
  );

  return (
    <Dialog
      header={
        approval?.success
          ? `Documents - ${ticketTypeLabel(approval.ticket.type)} | ${
              approval.ticket.employeeId
            } | ${approval.ticket.country}`
          : 'Documents'
      }
      footer={footer}
      onHide={onHide}
      visible={visible}
      style={{ width: '50vw' }}
      position="top"
    >
      {renderContent()}
    </Dialog>
  );
}
